import { CPA_OFFERS, type CPAOffer } from "@/lib/cpa-offers";
import { SPORTSBOOK_AFFILIATE_LINKS } from "@/lib/sportsbook-affiliate-links";
import { isBetDeepLink } from "@/lib/bet-links";
import { getOddsLiveClient } from "@/lib/odds-screen/live/client";
import type { BookId, Ladder, LadderTopic, MarketSpec, TopicState } from "@/lib/odds-screen/live/types";
import { railModel } from "./drill";

export type BetTarget = { readonly kind: "bet" | "signup"; readonly book: BookId; readonly href: string };

export type BetRequest = {
  readonly topic: LadderTopic;
  readonly spec: MarketSpec;
  readonly slot: number;
  readonly line: number | null;
  readonly outcome: number;
  readonly book: BookId;
  readonly decimal: number;
};

export type BetActions = {
  readonly hasAccount: (book: BookId) => boolean;
  readonly open: (request: BetRequest, onMissing: () => void) => void;
};

export type BetPick = { readonly book: BookId; readonly signup: boolean };

const RESOLVE_MS = 8000;
const POLL_MS = 50;

export function signupOffer(book: BookId): CPAOffer | null {
  return CPA_OFFERS.find((offer) => offer.id === book) ?? null;
}

function signupLink(book: BookId): string | null {
  return SPORTSBOOK_AFFILIATE_LINKS[book] ?? null;
}

export function betHref(row: { readonly link: string | null; readonly appLink: string | null }, isMobile: boolean): string | null {
  return isMobile ? row.appLink ?? row.link : row.link ?? row.appLink;
}

export function betTarget(book: BookId, href: string | null, hasAccount: boolean): BetTarget | null {
  const signup = signupLink(book);
  const deep = href !== null && isBetDeepLink(href) ? href : null;
  if (deep !== null && (hasAccount || signup === null)) return { kind: "bet", book, href: deep };
  return signup === null ? null : { kind: "signup", book, href: signup };
}

export function betPick(books: readonly BookId[], hasAccount: (book: BookId) => boolean): BetPick {
  const book = books.find(hasAccount) ?? books[0];
  return { book, signup: !hasAccount(book) && signupLink(book) !== null };
}

export function ladderLink(ladder: Ladder, request: Omit<BetRequest, "topic" | "decimal">, isMobile: boolean): string | null {
  const books = new Set([request.book]);
  const model = railModel(ladder, request.spec, request.slot, request.line, undefined, books, books, "american", 0);
  const row = model.columns.find((column) => column.outcome === request.outcome)?.rows[0];
  return row === undefined ? null : betHref(row, isMobile);
}

export function openBet(request: BetRequest, hasAccount: boolean, isMobile: boolean, onOpened: (target: BetTarget) => void, onMissing: () => void): void {
  const client = getOddsLiveClient();
  const resolve = (state: TopicState<Ladder>) => (state.phase === "ended" || state.value === null ? null : betTarget(request.book, ladderLink(state.value, request, isMobile), hasAccount));
  const held = client.read(request.topic);
  const now = (hasAccount ? null : betTarget(request.book, null, false)) ?? (held.value === null && held.phase !== "ended" ? undefined : resolve(held));
  if (now === null) {
    onMissing();
    return;
  }
  const tab = window.open("", "_blank");
  if (tab === null) {
    onMissing();
    return;
  }
  tab.opener = null;
  const land = (target: BetTarget | null) => {
    if (target === null) {
      tab.close();
      onMissing();
      return;
    }
    tab.location.replace(target.href);
    onOpened(target);
  };
  if (now !== undefined) {
    land(now);
    return;
  }
  const release = client.retain(request.topic);
  const deadline = Date.now() + RESOLVE_MS;
  const poll = setInterval(() => {
    const state = client.read(request.topic);
    if (state.value === null && state.phase !== "ended" && Date.now() < deadline) return;
    clearInterval(poll);
    release();
    if (!tab.closed) land(resolve(state));
  }, POLL_MS);
}
